import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  addCampaign,
  cancelCampaignById,
  duplicateCampaignById,
  getCampaignById,
  getCampaigns,
  updateCampaign,
} from '../services/campaigns.service';
import type {
  CampaignInsertInput,
  CampaignItem,
  CampaignsPagingResult,
  GetCampaignsParams,
  InsertCampaignResult,
  UpdateCampaignParams,
  UpdateCampaignResult,
} from '../types/campaigns.types';

export const useGetCampaigns = (
  params: GetCampaignsParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<CampaignsPagingResult>({
    queryKey: ['campaigns', params],
    queryFn: () => getCampaigns(params),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
};

export const useGetCampaignById = (campaignId: string) => {
  const id = campaignId.trim();

  return useGlobalQuery<CampaignItem>({
    queryKey: ['campaigns', 'detail', id],
    queryFn: () => getCampaignById(id),
    staleTime: 60 * 1000,
    enabled: Boolean(id),
  });
};

export const useGetCampaignDetails = (campaignId: string) => {
  const query = useGetCampaignById(campaignId);

  return {
    ...query,
    campaign: query.data,
  };
};

export const useInsertCampaign = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<InsertCampaignResult, Error, CampaignInsertInput>({
    mutationFn: addCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-rest'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
    },
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateCampaignResult, Error, UpdateCampaignParams>({
    mutationFn: updateCampaign,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-rest'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
      const itemId = variables.where.ItemId?.eq;
      if (itemId) {
        queryClient.invalidateQueries({ queryKey: ['campaigns', 'detail', itemId] });
      }
    },
  });
};

export const useDuplicateCampaign = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<InsertCampaignResult, Error, string>({
    mutationFn: duplicateCampaignById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-rest'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
    },
  });
};

export const useCancelCampaign = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateCampaignResult, Error, string>({
    mutationFn: cancelCampaignById,
    onSuccess: (_result, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'detail', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns-rest'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
    },
  });
};
